

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    Chip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewMerchantModal from 'components/NewMerchantModal';

const Merchants = () => {
    const [merchants, setMerchants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('merchants');

    useEffect(() => {
        fetchMerchants();
    }, []);

    const fetchMerchants = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/merchants/');
            setMerchants(response.data);
        } catch (error) {
            console.error('Error fetching merchants:', error);
            setSnackbar({ open: true, message: 'Error loading merchants', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveMerchant = async (payload) => {
        try {
            await axiosInstance.post('/merchants/', payload);
            setSnackbar({ open: true, message: 'Merchant created successfully', severity: 'success' });
            fetchMerchants();
        } catch (error) {
            console.error('Error saving merchant:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/merchant/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Merchant Name",
            accessor: "name",
            width: "20%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Merchant Type",
            accessor: "merchant_type",
            width: "15%",
            Cell: ({ value }) => value || '-'
        },
        {
            Header: "Email",
            accessor: "email",
            width: "15%",
            Cell: ({ value }) => value || '-'
        },
        {
            Header: "Phone",
            accessor: "phone",
            width: "12%",
            Cell: ({ value }) => value || '-'
        },
        {
            Header: "Payment Currency",
            accessor: "payment_currency",
            width: "12%",
            Cell: ({ value }) => value || '-'
        },
        {
            Header: "Payment Term",
            accessor: "payment_term",
            width: "12%",
            Cell: ({ value }) => value || 'NET 30'
        },
        {
            Header: "Partner",
            accessor: "is_partner",
            width: "8%",
            Cell: ({ value }) => (
                <Chip
                    label={value ? 'Yes' : 'No'}
                    size="small"
                    color={value ? 'success' : 'default'}
                />
            )
        },
        {
            Header: "Operating Country",
            accessor: "operating_country",
            width: "6%",
            Cell: ({ value }) => value || '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: merchants,
    }), [merchants]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Merchants</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Merchant
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : merchants.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Merchant" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewMerchantModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveMerchant}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default Merchants;

